import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Plane } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name.split(" ")[0]}.`);
      nav(res.user.role === "client" ? "/portal" : "/ops");
    } else {
      setErr(res.error);
    }
  };

  const quick = (e, p) => { setEmail(e); setPassword(p); };
  const inp = "w-full bg-[#FAF7F0] border border-[#E5DFD2] focus:border-[#A47E2A] focus:outline-none px-4 py-2.5 rounded-sm text-[#1A1D24] transition-colors text-sm";

  return (
    <div className="h-screen overflow-hidden bg-[#FAF7F0] flex text-[#1A1D24]">
      <div className="hidden md:block md:w-1/2 relative">
        <img src="https://images.pexels.com/photos/12802014/pexels-photo-12802014.jpeg?auto=compress&cs=tinysrgb&w=1400" className="w-full h-full object-cover" alt=""/>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1D24] via-[#1A1D24]/50 to-[#1A1D24]/20"/>
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="as-overline mb-3 !text-white/60">— AeroSphere Global</div>
          <div className="as-serif text-4xl font-light leading-tight text-white">Discretion, precision,<br/><span className="italic text-[#E5C158]">sovereignty.</span></div>
        </div>
      </div>
      <div className="w-full md:w-1/2 flex items-center justify-center px-6 py-6 overflow-y-auto">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-6">
            <Plane size={18} className="as-gold" strokeWidth={1.5}/>
            <span className="as-serif text-xl">AeroSphere <span className="as-gold">Global</span></span>
          </Link>
          <div className="as-overline mb-2">Sign In</div>
          <h1 className="as-serif text-3xl md:text-4xl font-light">Welcome back.</h1>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] mb-1.5">Email</div>
              <input required type="email" value={email} onChange={e => setEmail(e.target.value)} className={inp} data-testid="login-email"/>
            </div>
            <div>
              <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] mb-1.5">Password</div>
              <input required type="password" value={password} onChange={e => setPassword(e.target.value)} className={inp} data-testid="login-password"/>
            </div>
            {err && <div className="text-sm text-[#FCA5A5]" data-testid="login-error">{err}</div>}
            <button disabled={loading} className="as-btn-gold w-full py-3 rounded-sm as-mono text-xs uppercase tracking-[0.2em] disabled:opacity-60" data-testid="login-submit">
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
          <div className="mt-5">
            <div className="as-overline mb-2">Demo access</div>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => quick("admin@aerosphere.io", "admin123")} className="text-left border border-[#E5DFD2] hover:border-[#A47E2A] px-2.5 py-2 rounded-sm transition-colors" data-testid="demo-admin">
                <div className="text-xs text-[#1A1D24]">Admin</div>
                <div className="as-mono text-[9px] text-[#756E64] mt-0.5 truncate">admin123</div>
              </button>
              <button onClick={() => quick("ops@aerosphere.io", "ops123")} className="text-left border border-[#E5DFD2] hover:border-[#A47E2A] px-2.5 py-2 rounded-sm transition-colors" data-testid="demo-ops">
                <div className="text-xs text-[#1A1D24]">Operations</div>
                <div className="as-mono text-[9px] text-[#756E64] mt-0.5 truncate">ops123</div>
              </button>
              <button onClick={() => quick("client@aerosphere.io", "client123")} className="text-left border border-[#E5DFD2] hover:border-[#A47E2A] px-2.5 py-2 rounded-sm transition-colors" data-testid="demo-client">
                <div className="text-xs text-[#1A1D24]">Client</div>
                <div className="as-mono text-[9px] text-[#756E64] mt-0.5 truncate">client123</div>
              </button>
            </div>
          </div>
          <div className="mt-5 text-xs text-[#756E64]">
            New client? <Link to="/register" className="text-[#A47E2A] hover:underline" data-testid="login-to-register">Open an account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
